import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { apiFetch } from "../lib/api";

const EMPTY = { title: "", url: "", vertical: "all", type: "image", alt: "", caption: "", source: "", copyright_status: "owned", origin: "uploaded" };
const inputCls = "w-full border border-ink/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber";

export default function Media() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    apiFetch("/admin/media").then(setItems).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    await apiFetch("/admin/media", { method: "POST", body: JSON.stringify(form) });
    toast.success("Asset added");
    setForm(EMPTY);
    load();
  };

  const remove = async (id) => {
    await apiFetch(`/admin/media/${id}`, { method: "DELETE" });
    load();
  };

  const visible = filter === "all" ? items : items.filter((m) => m.vertical === filter);

  return (
    <div data-testid="admin-media">
      <Toaster position="top-center" richColors />
      <p className="eyebrow">Media</p>
      <h1 className="mt-2 font-serif text-3xl tracking-tight text-ink">Media library</h1>
      <div className="mt-6 flex flex-wrap gap-2">
        {["all", "dental", "consulting", "publishing"].map((v) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`media-filter-${v}`} className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] ${filter === v ? "border-burgundy bg-burgundy/5 text-burgundy" : "border-ink/15 text-ink/60"}`}>
            {v}
          </button>
        ))}
      </div>

      <form onSubmit={add} className="mt-8 grid gap-4 border border-ink/10 bg-warmwhite p-6 sm:grid-cols-2" data-testid="media-add-form">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50 sm:col-span-2">Register asset</p>
        <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="media-title-input" />
        <input required placeholder="URL (e.g. /images/photo.jpg)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} data-testid="media-url-input" />
        <input placeholder="Alt text" value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} className={inputCls} data-testid="media-alt-input" />
        <input placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className={inputCls} data-testid="media-caption-input" />
        <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className={inputCls} data-testid="media-vertical-select">
          <option value="all">All verticals</option>
          <option value="dental">Dental</option>
          <option value="consulting">Consulting</option>
          <option value="publishing">Publishing / Creative</option>
        </select>
        <select value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className={inputCls} data-testid="media-origin-select">
          <option value="uploaded">Uploaded</option>
          <option value="ai-generated">AI generated</option>
        </select>
        <button type="submit" className="btn-primary !px-5 !py-2.5 sm:col-span-2 sm:justify-self-start" data-testid="media-add-submit">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Asset
        </button>
      </form>

      {visible.length === 0 ? (
        <p className="mt-10 text-sm text-ink/50" data-testid="media-empty">No assets registered yet.</p>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {visible.map((m) => (
            <div key={m.id} className="border border-ink/10 bg-warmwhite" data-testid={`media-card-${m.id.slice(0, 8)}`}>
              {m.type === "image" && m.url && (
                <img src={m.url} alt={m.alt || m.title} loading="lazy" className="aspect-video w-full object-cover" />
              )}
              <div className="p-4">
                <p className="text-sm font-medium text-ink">{m.title}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-ink/50">{m.vertical} · {m.origin}</p>
                <button onClick={() => remove(m.id)} className="mt-3 text-ink/40 hover:text-red-600" aria-label="Delete asset" data-testid={`media-delete-${m.id.slice(0, 8)}`}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
